import React, { createContext, useState, useContext, useCallback } from 'react';
import { API_URL } from '../utils/config';
// Create the context
export const MenuContext = createContext();
// Create a custom hook to use the menu context
export const useMenu = () => useContext(MenuContext);
export const MenuProvider = ({ children }) => {
  const [menuCategories, setMenuCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  // Function to fetch menu categories from the API
  const fetchMenuCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Fetch fresh data from API
      const response = await fetch(`${API_URL}/categories/menu`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to fetch menu categories');
      }
      const result = await response.json();
      // Handle both wrapped { success, data } and raw array responses
      const data = result.data || result;
      if (!Array.isArray(data)) {
        throw new Error('Invalid data format received');
      }
      // Separate attributes and categories
      const attributes = data.filter(item => item.type === 'attribute');
      const categories = data.filter(item => item.type === 'category');
      const others = data.filter(item => item.type !== 'attribute' && item.type !== 'category');
      // Sort each group by ordering field
      const sortByOrdering = (a, b) => {
        if (a.ordering !== undefined && b.ordering !== undefined) {
          return a.ordering - b.ordering;
        }
        return a.id - b.id;
      };
      const sortedAttributes = [...attributes].sort(sortByOrdering);
      const sortedCategories = [...categories].sort(sortByOrdering);
      // Combine with attributes first, then categories, then others
      const sortedData = [...sortedAttributes, ...sortedCategories, ...others];
      // Update timestamp
      const now = new Date();
      setLastUpdated(now);
      // Store in state
      setMenuCategories(sortedData);
      return sortedData; // Return the categories for use by the caller
    } catch (error) {
      setError(error.message);
      setMenuCategories([]);
      return []; // Return empty array in case of error
    } finally {
      setLoading(false);
    }
  }, []);
  // Function to refresh the menu categories
  const refreshMenuCategories = useCallback(() => {
    return fetchMenuCategories();
  }, [fetchMenuCategories]);
  // The context value that will be supplied to any descendants of this provider
  const contextValue = {
    menuCategories,
    loading,
    error,
    lastUpdated,
    fetchMenuCategories,
    refreshMenuCategories
  };
  return (
    <MenuContext.Provider value={contextValue}>
      {children}
    </MenuContext.Provider>
  );
};
export default MenuProvider;
